import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HitlService, { HitlQueueItem, HitlQueueResponse, formatDaysAgo, stageReason } from './HitlService';
import HitlReviewDetailsModal from './HitlReviewDetailsModal';

/**
 * HITL Review Queue (spec watchlist-hitl-review.md §8, signal-pools §9). Unified queue showing both
 * stage_transition and pool_classification reviews, with a review_type tab/filter. Pool rows and
 * stage rows render with distinct context (§9.2). Bulk approve/reject + per-row detail modal.
 * institute_id comes from the URL (?institute_id=...), like the report page.
 */
const TABS = [
    { key: 'all', label: 'All Reviews' },
    { key: 'pool_classification', label: 'Pool Reviews' },
    { key: 'stage_transition', label: 'Stage Reviews' },
];

const HitlReviewQueuePage = () => {
    const [params, setParams] = useSearchParams();
    const instituteId = params.get('institute_id');
    const filterInstituteId = params.get('filter_institute_id');
    const reviewType = params.get('review_type') || 'all';
    const status = params.get('status') || 'pending';

    const [data, setData] = useState<HitlQueueResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [detail, setDetail] = useState<HitlQueueItem | null>(null);
    const [bulkBusy, setBulkBusy] = useState(false);

    const page = Number(params.get('page')) || 1;

    const load = useCallback(() => {
        if (!instituteId) { setError('institute_id is required in the URL.'); setLoading(false); return; }
        setLoading(true);
        setError(null);
        HitlService.getQueue(instituteId, {
            review_type: reviewType, status,
            outcome: params.get('outcome') || undefined,
            proposed_stage: params.get('proposed_stage') || undefined,
            pool_code: params.get('pool_code') || undefined,
            page,
        }, filterInstituteId)
            .then(setData)
            .catch((e) => setError(e?.response?.data?.error?.message || e?.message || 'Failed to load review queue.'))
            .finally(() => setLoading(false));
    }, [instituteId, filterInstituteId, reviewType, status, page, params]);

    useEffect(() => { load(); setSelected(new Set()); }, [load]);

    const setFilter = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value === '' || value == null) next.delete(key); else next.set(key, value);
        if (key !== 'page') next.delete('page');
        setParams(next);
    };

    const toggle = (id: number) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => {
        if (!data) return;
        setSelected((s) => (data.items.every((i) => s.has(i.queue_id)) ? new Set() : new Set(data.items.map((i) => i.queue_id))));
    };

    const bulk = async (kind: 'approve' | 'reject') => {
        if (selected.size === 0) return;
        // Reason is captured per-review, so bulk approve can't satisfy a reason-required transition.
        if (kind === 'approve' && (data?.items || []).some((i) => selected.has(i.queue_id) && i.config?.reason_required)) return;
        setBulkBusy(true);
        try {
            const ids = [...selected];
            await (kind === 'approve' ? HitlService.bulkApprove(ids, {}) : HitlService.bulkReject(ids, {}));
            setSelected(new Set());
            load();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || 'Bulk action failed.');
        } finally {
            setBulkBusy(false);
        }
    };

    if (!instituteId) return <div className="main p-4"><div className="alert alert-danger">institute_id is required in the URL.</div></div>;

    const items = data?.items || [];
    const allChecked = items.length > 0 && items.every((i) => selected.has(i.queue_id));
    // Bulk approve is unavailable when any selected review requires a reason (entered per-review).
    const bulkApproveBlocked = items.some((i) => selected.has(i.queue_id) && i.config?.reason_required);

    return (
        <div className="main p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <h2 className="m-0 font-22 font-600 text-dark-blue">HITL Review Queue</h2>
                <p className="m-0 font-13 text-secondary-emphasis">{data ? `${data.total_count}` : '…'} {status} review(s)</p>

                {/* Review-type tabs (§9.1) */}
                <div className="d-flex flex-wrap border-bottom mt-3 mb-3">
                    {TABS.map((t) => (
                        <div key={t.key} onClick={() => setFilter('review_type', t.key === 'all' ? '' : t.key)}
                            className={`px-4 py-2 cursor-pointer font-13 font-600 border-bottom border-2 ${reviewType === t.key ? 'text-dark-blue border-dark' : 'text-secondary-emphasis border-white'}`}>
                            {t.label}
                        </div>
                    ))}
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <select className="form-select form-select-sm rounded-3 font-12" style={{ width: 150 }} value={status} onChange={(e) => setFilter('status', e.target.value)}>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="expired">Expired</option>
                            <option value="all">All statuses</option>
                        </select>
                    </div>
                </div>

                {error && <div className="alert alert-danger font-13">{error}</div>}

                {selected.size > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-3 px-3 py-2 mb-3">
                        <span className="font-13 font-600 text-primary">{selected.size} selected</span>
                        <button
                            className="btn btn-sm btn-primary rounded-3"
                            disabled={bulkBusy || bulkApproveBlocked}
                            title={bulkApproveBlocked ? 'Some selected reviews require a reason — approve those individually via View.' : undefined}
                            onClick={() => bulk('approve')}>
                            Bulk Approve
                        </button>
                        <button className="btn btn-sm btn-outline-danger rounded-3" disabled={bulkBusy} onClick={() => bulk('reject')}>Bulk Reject</button>
                        <button className="btn btn-sm btn-link text-decoration-none" onClick={() => setSelected(new Set())}>Clear</button>
                        {bulkApproveBlocked && (
                            <span className="font-11 text-danger">Reason required on some — approve those individually.</span>
                        )}
                    </div>
                )}

                {loading ? (
                    <div className="font-14 text-secondary-emphasis py-4">Loading…</div>
                ) : (
                    <>
                        {items.length > 0 && (
                            <label className="d-flex align-items-center gap-2 font-12 text-muted mb-2">
                                <input type="checkbox" checked={allChecked} onChange={toggleAll} /> Select all on page
                            </label>
                        )}
                        <div className="d-flex flex-column gap-2">
                            {items.length === 0 ? (
                                <div className="bg-white border rounded-3 p-4 text-muted font-13">No reviews match the current filters.</div>
                            ) : items.map((it) => (
                                <div key={it.queue_id} className="bg-white border rounded-3 p-3 shadow-sm d-flex align-items-start gap-3">
                                    <input type="checkbox" className="mt-1" checked={selected.has(it.queue_id)} onChange={() => toggle(it.queue_id)} />
                                    <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="font-14 font-600 text-dark-blue">{it.name ?? '—'} <span className="font-12 font-400 text-secondary">· {it.program_and_year}</span></div>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-sm btn-light border rounded-3 font-12" onClick={() => setDetail(it)}>View</button>
                                            </div>
                                        </div>
                                        {it.review_type === 'pool_classification' ? (
                                            <>
                                                <div className="font-13 mt-1"><span className="font-600">{it.pool_label}</span> · {it.current_value} → <span className="font-600 text-primary">{it.proposed_value}</span> · proposed {formatDaysAgo(it.days_pending)}</div>
                                                {it.ambiguity_flag && <div className="font-12 text-warning mt-1">🚩 {it.ambiguity_flag.replace(/_/g, ' ')}{it.ambiguity_reason ? ` — ${it.ambiguity_reason}` : ''}</div>}
                                            </>
                                        ) : (
                                            <>
                                                <div className="font-13 mt-1"><span className="font-600">{it.outcome_label}</span> · {it.current_stage_label || '—'} → <span className="font-600 text-primary">{it.proposed_stage_label}</span> · proposed {formatDaysAgo(it.days_pending)}</div>
                                                {(stageReason(it.proposed_stage) || it.rule_trace) && (
                                                    <div className="font-12 text-muted mt-1" title={it.rule_trace || undefined}>
                                                        {stageReason(it.proposed_stage) || it.rule_trace}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {data && data.total_pages > 1 && (
                            <div className="d-flex align-items-center justify-content-between mt-3">
                                <span className="font-12 text-muted">Page {data.page} of {data.total_pages}</span>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>← Prev</button>
                                    <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page >= data.total_pages} onClick={() => setFilter('page', String(page + 1))}>Next →</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {detail && <HitlReviewDetailsModal item={detail} onClose={() => setDetail(null)} onDone={() => { setDetail(null); load(); }} />}
        </div>
    );
};

export default HitlReviewQueuePage;
